<template>
  <div class="data-list">
    <div class="data-list__head">
      <div>
        <h1 class="data-list__title">
          {{ title }}
        </h1>
        <div class="data-list__count">
          <template v-if="count !== null">
            {{ count.toLocaleString('fr-FR') }} résultat{{ count > 1 ? 's' : '' }}
            <span v-if="total !== null && total !== count"> sur {{ total.toLocaleString('fr-FR') }}</span>
          </template>
          <template v-else>
            Chargement…
          </template>
        </div>
      </div>
      <div class="data-list__actions">
        <slot name="actions" />
      </div>
    </div>

    <slot name="summary" />

    <div
      v-if="filters.length"
      class="data-list__filters">
      <template v-for="filter in filters">
        <v-text-field
          v-if="filter.type === 'search'"
          :key="filter.key"
          v-model="values[filter.key]"
          :placeholder="filter.label"
          :style="{ maxWidth: (filter.width || 280) + 'px' }"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          @input="onSearch" />
        <v-select
          v-else-if="filter.type === 'select'"
          :key="filter.key"
          v-model="values[filter.key]"
          :items="filter.items"
          :label="filter.label"
          :style="{ maxWidth: (filter.width || 190) + 'px' }"
          outlined
          dense
          hide-details
          clearable
          @change="applyFilters" />
        <v-text-field
          v-else-if="filter.type === 'month'"
          :key="filter.key"
          v-model="values[filter.key]"
          :label="filter.label"
          :style="{ maxWidth: (filter.width || 190) + 'px' }"
          type="month"
          outlined
          dense
          hide-details
          clearable
          @change="applyFilters" />
        <remote-select
          v-else-if="filter.type === 'remote'"
          :key="filter.key"
          v-model="values[filter.key]"
          :resource="filter.resource"
          :label="filter.label"
          :item-name="filter.itemName"
          :style="{ maxWidth: (filter.width || 240) + 'px' }"
          @input="applyFilters" />
      </template>
      <v-btn
        v-if="hasActiveFilters"
        text
        small
        color="primary"
        @click="resetFilters">
        Réinitialiser
      </v-btn>
    </div>

    <div class="data-list__table">
      <v-data-table
        :headers="headers"
        :items="items"
        :item-key="itemKey"
        :loading="loading"
        :options.sync="options"
        :server-items-length="count || 0"
        :footer-props="{ 'items-per-page-options': [25, 50, 100], 'items-per-page-text': 'Lignes par page' }"
        :class="{ 'is-clickable': !!rowTo }"
        loading-text="Chargement…"
        no-data-text="Aucun résultat"
        fixed-header
        @click:row="onRowClick">
        <template
          v-for="column in columns"
          #[`item.${column.key}`]="{ item }">
          <slot
            :name="column.key"
            :item="item">
            {{ cellValue(item, column) }}
          </slot>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
  import { debounce, get } from 'lodash'
  import RemoteSelect from './RemoteSelect'

  // Liste paginée côté serveur, avec filtres synchronisés dans l'URL
  export default {
    name: 'DataList',
    components: { RemoteSelect },
    props: {
      title: {
        type: String,
        required: true
      },
      resource: {
        type: String,
        required: true
      },
      // 'api' (vadimm-api) ou 'billing' (service de facturation)
      client: {
        type: String,
        default: 'api'
      },
      columns: {
        type: Array,
        required: true
      },
      filters: {
        type: Array,
        default: () => []
      },
      fixedParams: {
        type: Object,
        default: () => ({})
      },
      itemKey: {
        type: String,
        default: 'pk'
      },
      rowTo: {
        type: Function,
        default: null
      }
    },
    data () {
      const query = this.$route.query
      const values = {}
      this.filters.forEach(f => { values[f.key] = query[f.key] || null })
      return {
        values,
        items: [],
        count: null,
        total: null,
        loading: false,
        options: {
          page: parseInt(query.page) || 1,
          itemsPerPage: parseInt(query.per_page) || 25
        },
        requestId: 0
      }
    },
    computed: {
      headers () {
        return this.columns.map(c => ({
          text: c.label,
          value: c.key,
          sortable: false,
          width: c.width,
          align: c.align || 'start',
          class: c.class
        }))
      },
      hasActiveFilters () {
        return Object.values(this.values).some(v => v)
      },
      http () {
        return this.client === 'billing' ? this.$billingApi : this.$api.axios
      }
    },
    watch: {
      options: {
        deep: true,
        handler () {
          this.fetch()
        }
      }
    },
    created () {
      this.onSearch = debounce(this.applyFilters, 350)
    },
    methods: {
      cellValue (item, column) {
        const value = get(item, column.key)
        if (column.format) return column.format(value, item)
        return value === null || value === undefined || value === '' ? '—' : value
      },
      applyFilters () {
        if (this.options.page !== 1) {
          this.options = { ...this.options, page: 1 }
        } else {
          this.fetch()
        }
      },
      resetFilters () {
        Object.keys(this.values).forEach(k => { this.values[k] = null })
        this.applyFilters()
      },
      syncQuery () {
        const query = { ...this.$route.query, page: String(this.options.page), per_page: String(this.options.itemsPerPage) }
        Object.entries(this.values).forEach(([k, v]) => {
          if (v) query[k] = v
          else delete query[k]
        })
        if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
          this.$router.replace({ query }).catch(() => {})
        }
      },
      async fetch () {
        const requestId = ++this.requestId
        const params = { ...this.fixedParams, page: this.options.page, per_page: this.options.itemsPerPage }
        this.filters.forEach(f => {
          const value = this.values[f.key]
          if (!value) return
          if (f.toParams) Object.assign(params, f.toParams(value))
          else params[f.param || f.key] = value
        })
        this.syncQuery()
        this.loading = true
        try {
          const { data } = await this.http.get(this.resource, { params })
          if (requestId !== this.requestId) return
          // certaines routes (facturation) renvoient un simple tableau
          const items = Array.isArray(data) ? data : data.items
          this.items = items
          if (Array.isArray(data)) {
            this.count = items.length
            this.total = null
          } else if (data.count !== undefined) {
            this.count = data.count
            this.total = data.total === undefined ? null : data.total
          } else {
            // facturation : « total » = taille de la page, « big_total » = nombre total
            this.count = data.big_total === undefined ? items.length : data.big_total
            this.total = null
          }
        } catch (e) {
          if (requestId !== this.requestId) return
          this.items = []
          this.count = 0
          this.$awn.alert('Impossible de charger la liste')
        } finally {
          if (requestId === this.requestId) this.loading = false
        }
      },
      reload () {
        this.fetch()
      },
      onRowClick (item) {
        if (this.rowTo) {
          const to = this.rowTo(item)
          if (to) this.$router.push(to)
        }
      }
    }
  }
</script>

<style scoped>
  .data-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .data-list__head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
  }
  .data-list__title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.8px;
    line-height: 1.15;
  }
  .data-list__count {
    margin-top: 4px;
    color: #7a7a85;
    font-size: 13.5px;
  }
  .data-list__actions {
    display: flex;
    gap: 8px;
  }
  .data-list__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
  }
  .data-list__filters > * {
    flex: 1 1 180px;
  }
  .data-list__filters > .v-btn {
    flex: none;
  }
  .data-list__table {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #f0f0f2;
    border-radius: 18px;
    overflow: hidden;
  }
  .data-list__table >>> .v-data-table {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .data-list__table >>> .v-data-table__wrapper {
    flex: 0 1 auto;
    min-height: 0;
    overflow: auto;
  }
  .data-list__table >>> th {
    background: white !important;
    color: #7a7a85 !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    white-space: nowrap;
    height: 44px !important;
    border-bottom: 1px solid #f0f0f2 !important;
  }
  .data-list__table >>> td {
    font-size: 13.5px !important;
    height: 52px !important;
    border-bottom: 1px solid #f4f4f5 !important;
  }
  .data-list__table >>> tbody tr:last-child td {
    border-bottom: 0 !important;
  }
  .data-list__table >>> .is-clickable tbody tr {
    cursor: pointer;
  }
  .data-list__table >>> tbody tr:hover {
    background: #fafafa !important;
  }
  .data-list__table >>> .v-data-table__progress th {
    height: auto !important;
  }
  .data-list__table >>> .v-data-footer {
    flex: none;
    height: 44px;
    border-top: 1px solid #f0f0f2 !important;
    font-size: 12px;
    color: #7a7a85;
    padding: 0 8px;
  }
  .data-list__table >>> .v-data-footer__select .v-select {
    margin: 0 0 0 12px !important;
    padding: 0;
  }
  .data-list__table >>> .v-data-footer__select .v-input__slot {
    margin: 0;
  }
  .data-list__table >>> .v-data-footer__select .v-input__slot::before,
  .data-list__table >>> .v-data-footer__select .v-input__slot::after {
    display: none;
  }
  .data-list__table >>> .v-data-footer__select .v-select__selection {
    font-size: 12px;
  }
  .data-list__table >>> .v-data-footer .v-btn {
    border-radius: 8px;
  }
</style>
